import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Divider,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  IconButton,
  Grid,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../API/API";
import { BASE_URL } from "../API/Constants";
import { toast } from "react-toastify";

const api = new API();

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2', contrastText: '#ffffff' },
    secondary: { main: '#424242', contrastText: '#ffffff' },
    error: { main: '#d32f2f' },
    warning: { main: '#ed6c02' },
    info: { main: '#0288d1' },
    success: { main: '#2e7d32' },
    background: { default: '#f8f9fa', paper: '#ffffff' },
    text: { primary: '#212529', secondary: '#6c757d' }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 6, fontWeight: 600 },
        containedPrimary: { backgroundColor: '#1976d2', color: '#ffffff', '&:hover': { backgroundColor: '#115293' } },
        outlinedPrimary: { borderColor: '#1976d2', color: '#1976d2', '&:hover': { backgroundColor: '#e3f2fd', borderColor: '#1976d2' } },
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 8, backgroundImage: 'none' }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 6 }
      }
    }
  }
});

export default function GuestImages() {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    phone_number: "",
    room_number: "",
    status: "waiting",
  });
  
  // New images being uploaded
  const [images, setImages] = useState([]);
  // Existing images on the server (for editing)
  const [existingImages, setExistingImages] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [fetchingRooms, setFetchingRooms] = useState(false);
  const [autoLookupLoading, setAutoLookupLoading] = useState(false);
  
  // Edit State
  const [editId, setEditId] = useState(null);
  
  // Image Preview Modal State
  const [openImage, setOpenImage] = useState(null);
  
  // Upload Source Selection Dialog State
  const [openSourceDialog, setOpenSourceDialog] = useState(false);
  
  // Refs for hidden inputs
  const cameraInputRef = React.useRef(null);
  const galleryInputRef = React.useRef(null);

  useEffect(() => {
    fetchAvailableRooms();
  }, []);

  // Handle edit item passed via location.state
  useEffect(() => {
    if (location.state && location.state.editItem) {
      const item = location.state.editItem;
      setEditId(item.id);
      setFormData({
        phone_number: item.phone_number || "",
        room_number: item.room_number || "",
        status: item.status || "waiting",
      });
      setImages([]);
      setExistingImages(item.images || []);
      
      // Add room to available rooms if not present
      if (item.room_number) {
        setAvailableRooms((prev) => {
          const exists = prev.some((r) => r.room_number === item.room_number);
          if (!exists) {
            return [...prev, { room_number: item.room_number, id: `auto-${item.room_number}` }];
          }
          return prev;
        });
      }
    }
  }, [location.state]);

  const fetchAvailableRooms = async () => {
    setFetchingRooms(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await api.get("api/room-masters/available-rooms", { date: today });
      if (response && response.data) {
        setAvailableRooms(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching available rooms:", error);
    } finally {
      setFetchingRooms(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Auto-lookup guest data when 10-digit phone is entered
  const handlePhoneAutoLookup = async (phone) => {
    setAutoLookupLoading(true);
    try {
      const res = await api.get(`api/guest-images?phone_number=${phone}`);
      const records = res?.data?.data || res?.data || [];
      const list = Array.isArray(records) ? records : [];
      if (list.length > 0) {
        const latest = list[list.length - 1]; // get most recent record

        setFormData((prev) => ({
          ...prev,
          room_number: latest.room_number || prev.room_number,
          status: latest.status || prev.status,
        }));

        if (latest.images && latest.images.length > 0) {
          setExistingImages(latest.images);
        }

        if (latest.room_number) {
          setAvailableRooms((prev) => {
            const exists = prev.some((r) => r.room_number === latest.room_number);
            if (!exists) {
              return [...prev, { room_number: latest.room_number, id: `auto-${latest.room_number}` }];
            }
            return prev;
          });
        }

        toast.info(
          `✅ Guest found! Room ${latest.room_number} & ${latest.images?.length || 0} image(s) auto-filled.`,
          { autoClose: 3000 }
        );
      } else {
        toast.info("No previous record found for this number.", { autoClose: 2000 });
      }
    } catch (e) {
      // silently ignore lookup errors
    } finally {
      setAutoLookupLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setImages(prev => [...prev, ...newFiles]);
  };

  const removeSelectedImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setEditId(null);
    setFormData({ phone_number: "", room_number: "", status: "waiting" });
    setImages([]);
    setExistingImages([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = new FormData();
    data.append("phone_number", formData.phone_number);
    data.append("room_number", formData.room_number);
    data.append("status", formData.status);
    
    if (images.length > 0) {
      images.forEach((image) => {
        data.append("images[]", image);
      });
    }

    if (editId) {
      data.append("existing_images", JSON.stringify(existingImages));
    }

    data.isMultipart = true;

    try {
      let response;
      if (editId) {
        data.append("_method", "PUT");
        response = await api.post(`api/guest-images/${editId}`, data);
      } else {
        response = await api.post("api/guest-images", data);
      }

      if (response) {
        toast.success(editId ? "Guest images updated successfully!" : "Guest images saved successfully!");
        resetForm();
        navigate("/saved-submissions");
      }
    } catch (error) {
      console.error("Error saving guest images:", error);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (img) => {
    if (!img) return "";
    let path = typeof img === 'string' ? img : (img.url || "");
    if (path.startsWith('http')) return path;
    
    const storagePath = "storage/app/public/";
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    
    if (cleanPath.startsWith(storagePath)) {
      return `${BASE_URL}${cleanPath}`;
    }
    return `${BASE_URL}${storagePath}${cleanPath}`;
  };

  const isFormValid = 
    formData.phone_number.length === 10 && 
    Boolean(formData.room_number) && 
    (images.length > 0 || existingImages.length > 0);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ width: '100%', mt: { xs: 0.5, md: 2 }, mb: 3, px: { xs: 1, sm: 2 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, md: 2.5 }, flexWrap: 'wrap', gap: 1 }}>
          <Typography 
            sx={{ 
              fontWeight: 700, 
              color: '#1565c0', 
              fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
              letterSpacing: 0.3
            }}
          >
            {editId ? "Edit Guest Entry" : "Add Guest Entry"}
          </Typography>

          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<FormatListBulletedIcon />}
            onClick={() => navigate("/saved-submissions")}
            sx={{
              fontSize: isMobile ? '0.78rem' : '0.875rem',
              py: isMobile ? 0.6 : 0.8,
              px: isMobile ? 1.2 : 2,
              borderRadius: 1.5,
              fontWeight: 600
            }}
          >
            Saved Submissions
          </Button>
        </Box>

        {/* Form Container Card */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 2, md: 3 }, 
              border: '1px solid #d0d7de', 
              borderRadius: 2, 
              width: '100%',
              maxWidth: { xs: '100%', md: 550 }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', md: '1.1rem' }, color: '#24292f' }}>
                {editId ? "Update Existing Entry" : "Add New Guest Details"}
              </Typography>
              {editId && (
                <Button 
                  size="small" 
                  variant="text" 
                  color="error" 
                  startIcon={<ArrowBackIcon sx={{ fontSize: '0.9rem !important' }} />}
                  onClick={() => { resetForm(); navigate("/saved-submissions"); }} 
                  sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0 }}
                >
                  Cancel Edit
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                size="small"
                label="Phone Number"
                name="phone_number"
                value={formData.phone_number}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  if (val.length <= 10) {
                    handleInputChange({ target: { name: 'phone_number', value: val } });
                    if (val.length === 10 && !editId) {
                      handlePhoneAutoLookup(val);
                    }
                  }
                }}
                margin="dense"
                required
                variant="outlined"
                placeholder="10 digit number"
                inputProps={{ maxLength: 10, inputMode: 'numeric', style: { fontSize: isMobile ? '0.85rem' : '0.95rem' } }}
                InputLabelProps={{ style: { fontSize: isMobile ? '0.85rem' : '0.95rem' } }}
                InputProps={{
                  endAdornment: autoLookupLoading ? (
                    <CircularProgress size={14} sx={{ mr: 1 }} />
                  ) : null,
                }}
                helperText={
                  autoLookupLoading
                    ? "Looking up guest..."
                    : formData.phone_number.length === 10
                    ? "✓ Phone number complete"
                    : `${formData.phone_number.length}/10 digits`
                }
                FormHelperTextProps={{ style: { fontSize: '0.7rem', marginTop: 2 } }}
                sx={{ mb: 1.5 }}
              />

              <Autocomplete
                fullWidth
                size="small"
                options={availableRooms}
                getOptionLabel={(option) => option.room_number || ""}
                value={availableRooms.find(r => r.room_number === formData.room_number) || null}
                onChange={(event, newValue) => {
                  setFormData(prev => ({ ...prev, room_number: newValue ? newValue.room_number : "" }));
                }}
                loading={fetchingRooms}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Room Number"
                    margin="dense"
                    required
                    variant="outlined"
                    inputProps={{ ...params.inputProps, style: { ...params.inputProps.style, fontSize: isMobile ? '0.85rem' : '0.95rem' } }}
                    InputLabelProps={{ style: { fontSize: isMobile ? '0.85rem' : '0.95rem' } }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {fetchingRooms ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                sx={{ mb: 1.5 }}
              />
              
              <Box sx={{ mt: 2, mb: 1 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setOpenSourceDialog(true)}
                  sx={{
                    py: isMobile ? 1 : 1.4,
                    borderStyle: 'dashed',
                    borderWidth: 1.5,
                    fontSize: isMobile ? '0.82rem' : '0.9rem',
                    textTransform: 'none',
                    borderColor: (images.length > 0 || existingImages.length > 0) ? 'primary.main' : '#ccc',
                    bgcolor: (images.length > 0 || existingImages.length > 0) ? '#e3f2fd' : 'transparent',
                    color: (images.length > 0 || existingImages.length > 0) ? 'primary.main' : 'text.secondary',
                    '&:hover': { borderStyle: 'dashed', bgcolor: '#e3f2fd' }
                  }}
                >
                  {editId ? "Add More Images" : "Select Guest Images"}
                </Button>

                {/* Hidden File Inputs */}
                <input
                  type="file"
                  hidden
                  ref={cameraInputRef}
                  onChange={(e) => {
                    handleFileChange(e);
                    setOpenSourceDialog(false);
                  }}
                  accept="image/*"
                  capture="environment"
                />
                <input
                  type="file"
                  multiple
                  hidden
                  ref={galleryInputRef}
                  onChange={(e) => {
                    handleFileChange(e);
                    setOpenSourceDialog(false);
                  }}
                  accept="image/*"
                />
                
                {/* Selected/Existing Image Previews */}
                {(images.length > 0 || existingImages.length > 0) && (
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1, overflowX: 'auto', py: 0.5, px: 0.5 }}>
                    {/* Existing Images */}
                    {existingImages.map((img, idx) => (
                      <Box key={`ex-${idx}`} sx={{ position: 'relative', minWidth: 52 }}>
                        <Box 
                          component="img" 
                          src={getImageUrl(img)} 
                          sx={{ 
                            width: 52, height: 52, borderRadius: 1, objectFit: 'cover', 
                            border: '1px solid #ddd', cursor: 'pointer',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'scale(1.05)' }
                          }}
                          onClick={() => setOpenImage(getImageUrl(img))}
                        />
                        <IconButton 
                          size="small" 
                          onClick={(e) => { e.stopPropagation(); removeExistingImage(idx); }}
                          sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' }, width: 18, height: 18, p: 0 }}
                        >
                          <CloseIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                        <Typography variant="caption" sx={{ position: 'absolute', bottom: 1, left: 1, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', px: 0.4, borderRadius: 0.5, fontSize: '7px' }}>Saved</Typography>
                      </Box>
                    ))}
                    
                    {/* New Uploads */}
                    {images.map((file, idx) => (
                      <Box key={`new-${idx}`} sx={{ position: 'relative', minWidth: 52 }}>
                        <Box 
                          component="img" 
                          src={URL.createObjectURL(file)} 
                          sx={{ 
                            width: 52, height: 52, borderRadius: 1, objectFit: 'cover', 
                            border: '1.5px solid', borderColor: 'primary.main',
                            cursor: 'pointer',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'scale(1.05)' }
                          }}
                          onClick={() => setOpenImage(URL.createObjectURL(file))}
                        />
                        <IconButton 
                          size="small" 
                          onClick={(e) => { e.stopPropagation(); removeSelectedImage(idx); }}
                          sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' }, width: 18, height: 18, p: 0 }}
                        >
                          <CloseIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                        <Typography variant="caption" sx={{ position: 'absolute', bottom: 1, left: 1, bgcolor: '#1976d2', color: 'white', px: 0.4, borderRadius: 0.5, fontSize: '7px' }}>New</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              
              {/* Validation status indicator */}
              {!loading && (
                <Box sx={{ mt: 1, mb: 0.5, textAlign: 'center' }}>
                  {formData.phone_number.length < 10 ? (
                    <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 600, fontSize: '0.72rem' }}>
                      ⚠️ Enter 10-digit phone number
                    </Typography>
                  ) : !formData.room_number ? (
                    <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 600, fontSize: '0.72rem' }}>
                      ⚠️ Select a room number
                    </Typography>
                  ) : (images.length === 0 && existingImages.length === 0) ? (
                    <Typography variant="caption" sx={{ color: '#ed6c02', fontWeight: 600, fontSize: '0.72rem' }}>
                      ⚠️ Upload at least 1 guest image to enable Save
                    </Typography>
                  ) : (
                    <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 700, fontSize: '0.72rem' }}>
                      ✓ All details complete! Click below to submit.
                    </Typography>
                  )}
                </Box>
              )}

              <Button 
                type="submit" 
                color={editId ? "success" : "primary"} 
                variant="contained" 
                fullWidth 
                disabled={!isFormValid || loading} 
                size="medium" 
                sx={{ 
                  mt: 1, 
                  py: 1.1, 
                  fontWeight: 700, 
                  borderRadius: 1.5, 
                  fontSize: isMobile ? '0.88rem' : '0.95rem', 
                  textTransform: 'none', 
                  opacity: (isFormValid && !loading) ? 1 : 0.45,
                  boxShadow: (isFormValid && !loading) ? (editId ? '0 4px 12px rgba(46,125,50,0.35)' : '0 4px 12px rgba(25,118,210,0.35)') : 'none',
                  transition: 'all 0.3s ease',
                  '&.Mui-disabled': {
                    bgcolor: '#c2c2c2',
                    color: '#ffffff',
                    opacity: 0.45
                  }
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : (editId ? "Update Submission" : "Save Submission")}
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Image Preview Modal */}
        <Dialog open={Boolean(openImage)} onClose={() => setOpenImage(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible' } }}>
          <IconButton onClick={() => setOpenImage(null)} sx={{ position: 'absolute', right: -12, top: -12, bgcolor: 'white', boxShadow: 2, '&:hover': { bgcolor: '#f5f5f5' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <DialogContent sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
            {openImage && <Box component="img" src={openImage} sx={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 1, objectFit: 'contain' }} />}
          </DialogContent>
          <Box sx={{ px: 2, pb: 2, textAlign: 'center' }}>
            <Button variant="contained" onClick={() => setOpenImage(null)} sx={{ borderRadius: 1.5, textTransform: 'none', px: 3, py: 0.6, fontSize: '0.85rem' }}>Close</Button>
          </Box>
        </Dialog>

        {/* Upload Source Selection Dialog */}
        <Dialog 
          open={openSourceDialog} 
          onClose={() => setOpenSourceDialog(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              width: '90%',
              maxWidth: 300,
              p: 1
            }
          }}
        >
          <DialogContent sx={{ p: 2 }}>
            <Typography variant="subtitle1" align="center" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
              Upload Image
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box 
                  onClick={() => cameraInputRef.current?.click()}
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: '#f5f5f5',
                    border: '1px solid #d0d7de',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#e3f2fd', borderColor: '#1976d2' }
                  }}
                >
                  <Box sx={{ fontSize: '2rem' }}>📷</Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#1976d2', textTransform: 'uppercase' }}>Camera</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box 
                  onClick={() => galleryInputRef.current?.click()}
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: '#f5f5f5',
                    border: '1px solid #d0d7de',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#e3f2fd', borderColor: '#1976d2' }
                  }}
                >
                  <Box sx={{ fontSize: '2rem' }}>📁</Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#1976d2', textTransform: 'uppercase' }}>Gallery</Typography>
                </Box>
              </Grid>
            </Grid>
            <Button 
              fullWidth 
              size="small"
              onClick={() => setOpenSourceDialog(false)}
              sx={{ mt: 2, textTransform: 'none', color: 'text.secondary', fontSize: '0.8rem' }}
            >
              Cancel
            </Button>
          </DialogContent>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}
